import { createClient, RealtimeChannel, SupabaseClient } from "@supabase/supabase-js";
import {
  AccessLevelPermission,
  CreateSpace,
  CreateSpaceMeeting,
  MemberAuthorization,
  Organization,
  Space,
  SpaceMeeting,
  SpaceMeetingAttendee,
  UserProfile,
} from "../models";
import { first, omit, uniqBy } from "lodash";
import { DateTime } from "luxon";
import { logger } from "../utils/logger";
import { cadchatTokenStorage, useAddInStore, useTokenStore } from "../hooks";
import { envUtils } from "../utils/envUtils";

/** Roaming-settings key for the cached Supabase access token (used to skip the auth exchange). */
const SUPABASE_ACCESS_TOKEN_KEY = "cadchat-sb-access-token";

/** Re-exchange the Supabase token this many ms before it actually expires. */
const SUPABASE_TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

/** Decodes a JWT's `exp` claim (in milliseconds). Returns undefined if it can't be parsed. */
const decodeJwtExpiryMs = (jwt: string): number | undefined => {
  try {
    const payloadSegment = jwt.split(".")[1];
    if (!payloadSegment) return undefined;

    const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );

    const payload = JSON.parse(json);
    return typeof payload.exp === "number" ? payload.exp * 1000 : undefined;
  } catch {
    return undefined;
  }
};

type BroadcastPayload = {
  type: string;
  event: string;
  payload: {
    [key: string]: any;
  };
};

export type OnboardingBroadcastMessage = {
  type: string;
  isComplete: boolean;
  windowClosed: boolean;
};

/**
 * Supabase Storage image transform parameters for the image render endpoint.
 */
type SupabaseStorageImageTransform = {
  /**
   * The rendered image width in pixels.
   */
  width?: number;
  /**
   * The rendered image height in pixels.
   */
  height?: number;
  /**
   * How Supabase should fit the original image into the requested bounds.
   */
  resize?: "cover" | "contain" | "fill";
  /**
   * The rendered image quality from 20 to 100.
   */
  quality?: number;
};

const DEFAULT_AVATAR_IMAGE_TRANSFORM: SupabaseStorageImageTransform = {
  width: 256,
  height: 256,
  resize: "cover",
  quality: 80,
};

/**
 * The storage type identifier for files stored in Supabase Storage.
 *
 * This is used in the `file_metadata_storage_pointer` table to indicate that
 * a file is stored in Supabase Storage and to link the file metadata to the
 * correct storage location.
 */
const SUPABASE_STORAGE_TYPE = "SupabaseStorage";

/**
 * The name of the Supabase Storage bucket where user avatars are stored.
 */
export const SUPABASE_AVATAR_BUCKET = "user_avatars";

class SupabaseApi {
  private _client: SupabaseClient | undefined;
  private _accessLevelPermissions: AccessLevelPermission[] | undefined = undefined;
  private _currentUserListener: RealtimeChannel | undefined = undefined;
  private _onboardingBroadcastListener: RealtimeChannel | undefined = undefined;

  constructor() {}

  public async setSession() {
    try {
      // Fast path: reuse a cached, still-valid Supabase token to skip the auth-exchange round-trip.
      // This matters most in the on-send (commands) runtime, where the exchange is otherwise on the
      // critical path and contributes to Outlook's "taking longer than expected" prompt.
      const cachedToken = this.getCachedSupabaseToken();
      if (cachedToken) {
        this.initClientWithToken(cachedToken);
        return;
      }

      let token = undefined;

      try {
        const authToken = useTokenStore.getState().token;
        if (!authToken) {
          logger.error("No auth token found in Office storage");
          throw Error("Unable to parse id_token");
        }
        token = authToken?.id_token;
        if (!token) {
          logger.error("Unable to parse id_token from auth0_token");
          throw Error("Unable to parse id_token");
        }
      } catch (error) {
        logger.error("Error retrieving token from storage", error);
        throw Error("Error retrieving token from storage");
      }

      const response = await fetch(`${envUtils.SUPABASE_BASE_URL}/functions/v1/auth`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const responseBody = await response.text().catch(() => "");
        logger.error("Supabase auth endpoint rejected the token", response.status, responseBody);
        throw new Error(`Supabase auth failed (${response.status})`);
      }

      /** set the local token */
      const sb_token = await response.text();

      /** throw error if we don't have a token */
      if (!sb_token) {
        logger.error("No Supabase token received from auth endpoint");
        throw new Error("Unable to fetch supabase token.");
      }

      this.initClientWithToken(sb_token);

      // Cache the freshly exchanged token so subsequent sessions (including the on-send runtime,
      // which shares roaming settings) can reuse it and skip the exchange.
      await this.cacheSupabaseToken(sb_token);
    } catch (error) {
      logger.error("Error in setSession", error);
      if (error?.code === "PGRST301") {
        this.clearCachedSupabaseToken();
        useTokenStore.getState().removeToken();
      }
      throw error;
    }
  }

  /** Builds the Supabase client and applies the access token to auth, functions, and realtime. */
  private initClientWithToken(sb_token: string) {
    this._client = createClient(envUtils.SUPABASE_BASE_URL, envUtils.SUPABASE_ANON_KEY, {
      global: {
        headers: {
          Authorization: `Bearer ${sb_token}`,
        },
      },
    });

    /** set the users session */
    this._client.auth.setSession({
      access_token: sb_token,
      refresh_token: "",
    });

    /** because we use custom JWTs we need to set the authentication for functions and realtime. */
    this._client.functions.setAuth(sb_token);
    this._client.realtime.setAuth(sb_token);
  }

  /**
   * Returns a cached Supabase access token from roaming settings if one is stored and not within the
   * expiry buffer; otherwise undefined so the caller performs a fresh auth exchange.
   */
  private getCachedSupabaseToken(): string | undefined {
    try {
      const cached = cadchatTokenStorage.get<string>(SUPABASE_ACCESS_TOKEN_KEY);
      if (!cached) return undefined;

      const expiryMs = decodeJwtExpiryMs(cached);
      if (!expiryMs || Date.now() >= expiryMs - SUPABASE_TOKEN_EXPIRY_BUFFER_MS) {
        return undefined;
      }

      return cached;
    } catch (error) {
      logger.warn("Unable to read cached Supabase token", error);
      return undefined;
    }
  }

  private async cacheSupabaseToken(sb_token: string): Promise<void> {
    try {
      await cadchatTokenStorage.set(SUPABASE_ACCESS_TOKEN_KEY, sb_token);
    } catch (error) {
      logger.warn("Unable to cache Supabase token", error);
    }
  }

  private clearCachedSupabaseToken(): void {
    try {
      void cadchatTokenStorage.remove(SUPABASE_ACCESS_TOKEN_KEY);
    } catch (error) {
      logger.warn("Unable to clear cached Supabase token", error);
    }
  }

  public async login(): Promise<UserProfile> {
    try {
      if (!this._client) await this.setSession();
      if (!this._client) throw new Error("Client is not initialized");

      const { data, error } = await this._client.rpc("login_jwt").single();

      if (error) {
        logger.error("Error fetching user profile", error);
        throw error;
      }

      const userProfile = data as UserProfile;
      if (userProfile) {
        try {
          if (this._currentUserListener) this._currentUserListener.unsubscribe();

          this._currentUserListener = this._client.channel(
            `cadchat_adding:user_profile:${userProfile.id}`
          );
          this._currentUserListener
            .on(
              "postgres_changes",
              {
                event: "UPDATE",
                schema: "public",
                table: "user_profile",
                filter: `id=eq.${userProfile.id}`,
              },
              (payload) => {
                useAddInStore.getState().setUser(payload.new as UserProfile);
              }
            )
            .subscribe(() => {
              logger.debug("Subscribed to user profile changes...");
            });
        } catch (error) {
          logger.error("Error subscribing to realtime user profile changes", error);
        }
      }

      return data as UserProfile;
    } catch (error) {
      logger.error("Error in getUserProfile", error);
      if (error?.code === "PGRST301") {
        useTokenStore.getState().removeToken();
      }
      throw error;
    }
  }

  public async getCurrentUserId(): Promise<string | undefined> {
    try {
      if (!this._client) await this.setSession();
      if (!this._client) throw new Error("Client is not initialized");

      const { data, error } = await this._client.rpc("get_current_user").throwOnError().single();

      if (error) {
        logger.error("Error fetching user id", error);
        throw error;
      }

      return data as string | undefined;
    } catch (error) {
      logger.error("Error in getUserProfile", error);
      throw error;
    }
  }

  public async getUserProfileById(id: string | undefined) {
    try {
      if (!id) throw new Error("User id is required");
      if (!this._client) await this.setSession();
      if (!this._client) throw new Error("Client is not initialized");

      const { data, error } = await this._client
        .from("user_profile")
        .select("*")
        .eq("id", id)
        .throwOnError()
        .single();

      if (error) {
        logger.error("Error fetching user profile by ID", { id, error });
        throw error;
      }

      return data as UserProfile;
    } catch (error) {
      logger.error("Error in getUserProfileById", error);
      throw error;
    }
  }

  public async updateUserProfile(userProfile: UserProfile) {
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { data } = await this._client
      .from("user_profile")
      .update(userProfile)
      .eq("id", userProfile.id)
      .select("*")
      .returns<UserProfile>()
      .throwOnError()
      .single();

    return data;
  }

  /**
   * Gets the public URL for a file stored in Supabase Storage based on its metadata ID.
   * @param {string | undefined} file_metadata_id The ID of the file metadata for which to get the public URL.
   * @returns {string | undefined} The public URL of the file or undefined if not found.
   */
  public async getSupabaseStoragePublicUrl(file_metadata_id: string | undefined) {
    try {
      if (!file_metadata_id) throw new Error("File metadata id is required");
      if (!this._client) await this.setSession();
      if (!this._client) throw new Error("Client is not initialized");

      const { data: pointers, error } = await this._client
        .from("file_metadata_storage_pointer")
        .select("*")
        .eq("file_metadata_id", file_metadata_id)
        .eq("file_storage_type", SUPABASE_STORAGE_TYPE)
        .limit(1);

      if (error) {
        logger.error("Error fetching file metadata by ID", { file_metadata_id, error });
        throw error;
      }

      // Get the first pointer from the results since there should only be one.
      const pointer = pointers?.[0];
      if (!pointer) {
        return undefined;
      }

      const { data } = this._client.storage.from(pointer.share_name).getPublicUrl(pointer.uri, {
        transform: DEFAULT_AVATAR_IMAGE_TRANSFORM,
      });

      return this.normalizeSupabaseStoragePublicUrl(data.publicUrl);
    } catch (error) {
      logger.error("Error in getSupabaseStoragePublicUrl", error);
      throw error;
    }
  }

  /**
   * Returns the organizations this user has access to.
   *
   * @returns {Organization[]} Array of organizations
   */
  public async getOrganizations(): Promise<Organization[]> {
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { data, error } = await this._client
      .from("organization")
      .select("*")
      .returns<Organization[]>();

    /** for now throw errors but in the future log them or do something else */
    if (error) throw new Error("Error getting organizations");

    return uniqBy(data, "id");
  }

  /**
   * Returns the spaces that the current user can share within the associated
   * organization id.
   *
   * @param organizationId
   * @returns {Space[]} Array of shareable spaces
   */
  public async getShareableSpaces(organizationId: string): Promise<Space[]> {
    if (!organizationId || organizationId === "") throw new Error("Organization id is required");
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { data, error } = await this._client.rpc("get_shareable_spaces", {
      org_id: organizationId,
    });

    /** for now throw errors but in the future log them or do something else */
    if (error) throw new Error("Error getting shareable workspaces");

    const filteredData = data?.filter((space: Space) => !space.is_unavailable) ?? [];

    return uniqBy<Space>(
      filteredData.sort((a, b) => a.name.localeCompare(b.name)),
      "id"
    );
  }

  /**
   * Returns the space with the provided space id.
   *
   * @param {string} spaceId The id of the space to get.
   *
   * @returns {Space | undefined} The space or undefined if not found.
   */
  public async getSpace(spaceId: string, select = "*") {
    if (!spaceId || spaceId === "") throw new Error("Workspace id is required");
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { data, error } = await this._client
      .from("space")
      .select(select)
      .eq("id", spaceId)
      .returns<Space[]>();

    /** for now throw errors but in the future log them or do something else */
    if (error) throw new Error("Error getting space");

    return first(data);
  }

  /**
   * Creates a new space in the provided organization.
   *
   * @param {CreateSpace} createSpace the name and organization id that should be used to create the space.
   *
   * @returns {Space | undefined} The space or undefined if not created.
   */
  public async createSpace(createSpace: CreateSpace): Promise<string> {
    if (!createSpace) throw new Error("Space data is required");
    if (!createSpace?.organizationId)
      throw new Error("Organization Id is required to create a new workspace");
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    /** call the insert space rpc function */
    const { data: spaceId, error: createSpaceError } = await this._client
      .rpc("insert_space", {
        _name: createSpace.name,
        _organization_id: createSpace.organizationId,
      })
      .returns<string>()
      .single();

    /** for now throw errors but in the future log them or do something else */
    if (createSpaceError) throw new Error("Error creating space");

    /** return the space */
    return spaceId;
  }

  /**
   * Deletes the space with the provided id.
   *
   * @param {string} id The id of the space to delete.
   * @returns {boolean} True if the space was deleted.
   */
  public async deleteSpace(id: string) {
    if (!id || id === "") throw new Error("Space id is required");
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { error } = await this._client.from("space").delete().eq("id", id);

    if (error) throw new Error("Error deleting space");

    return true;
  }

  /**
   * Returns the space meeting by the event id if one exists.
   * If no space meeting is found, it will return undefined.
   *
   * @param eventId - The calendar providers event id to search for.
   * @returns {SpaceMeeting | undefined} The space meeting or undefined if not found.
   */
  public async getSpaceMeetingByEventId(eventId: string) {
    if (!eventId || eventId === "") return undefined;
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { data } = await this._client
      .from("space_meeting")
      .select("*")
      .eq("calendar_metadata->>eventId", eventId)
      .returns<SpaceMeeting[]>()
      .throwOnError();

    return first(data) ?? undefined;
  }

  /**
   *
   * @param meetingId
   * @returns
   */
  public async getSpaceMeeting(meetingId: string) {
    if (!meetingId || meetingId === "") throw new Error("Workspace Meeting id is required");
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { data, error } = await this._client
      .from("space_meeting")
      .select("*")
      .eq("id", meetingId)
      .returns<SpaceMeeting[]>();

    /** for now throw errors but in the future log them or do something else */
    if (error) throw new Error("Error getting space");

    return first(data);
  }

  /**
   * Creates a new space meeting based on the provided meeting data. This function will also create
   * a new space if the spaceId is not provided and the organizationId is provided. If you call
   * create space then this function there is a chance the database will send out two emails to
   * one for the invitation to the space and one for the invitation to the meeting. If you do not want
   * that interaction then you should use this method to create the meeting and the space.
   *
   * @param meeting
   * @returns {SpaceMeeting} The created space meeting.
   */
  public async createSpaceMeeting(meeting: CreateSpaceMeeting) {
    if (!meeting) throw new Error("Meeting data is required");
    if (!meeting?.space_id && !meeting.organization_id)
      throw new Error("A space or organization id is required to create a new meeting");
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    if (!meeting.space_id && meeting.organization_id) {
      const { data: spaceId, error: createSpaceError } = await this._client
        .rpc("insert_space", {
          _name: meeting.title,
          _organization_id: meeting.organization_id,
        })
        .returns<string>()
        .single();

      logger.debug("insert_space response:", spaceId, createSpaceError);

      if (!spaceId || createSpaceError) throw new Error("Error creating space");
      meeting.space_id = spaceId;
    }

    // Make sure that the emails are valid one more time before we
    // try to invite them to the space meeting.
    // In case there are any delimiter characters in the emails,
    // we remove them.
    const validEmailsToInvite = meeting.attendees
      .filter((email) => this.isEmail(email))
      .map((email) => email.trim().replace(",", "").toLowerCase())
      .filter((email) => email !== undefined && email !== null && email !== "");

    // prepare the formatted attendees for logging
    const formattedAttendees = validEmailsToInvite.map((email) => ({
      login_email: email,
      access_level: "EDITOR",
      can_download: true,
      can_share: true,
    }));

    logger.debug("formattedAttendees:", formattedAttendees);

    // Create the space meeting via the edge function. We pass calendar_metadata directly so the
    // function persists it on creation — this avoids a second follow-up UPDATE round-trip (~600ms)
    // on the send critical path. We can do this because the on-send handler already has the Outlook
    // event id before creating the meeting; historically the meeting had to be created first.
    const { data, error: createMeetingError } = await this._client.functions.invoke<{
      meeting: SpaceMeeting;
      meeting_attendees: SpaceMeetingAttendee[];
    }>("create-space-meeting", {
      body: {
        space_id: meeting.space_id,
        start_date: meeting.start_date,
        end_date: meeting.end_date,
        title: meeting.title,
        description: meeting.description,
        attendees: formattedAttendees,
        calendar_metadata: meeting.calendar_metadata,
      },
    });

    logger.debug("createSpaceMeeting response:", data, createMeetingError);

    /** for now throw errors but in the future log them or do something else */
    if (createMeetingError) {
      throw new Error(`Error getting creating meeting. ${createMeetingError?.message}`);
    }

    // The edge function now sets calendar_metadata during creation. Mirror it on the returned object
    // for callers that read it immediately (in case the response doesn't echo it back).
    if (meeting?.calendar_metadata && data?.meeting) {
      data.meeting.calendar_metadata = { ...meeting.calendar_metadata };
    }

    return data?.meeting as unknown as SpaceMeeting;
  }

  public async updateSpaceMeeting(meeting: SpaceMeeting) {
    if (!meeting) throw new Error("Meeting data is required");
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { data, error } = await this._client
      .from("space_meeting")
      .update(omit(meeting, ["id", "organization_id", "attendees"]))
      .eq("id", meeting.id)
      .select("*")
      .returns<SpaceMeeting[]>();

    if (error) throw new Error("Error updating space meeting");

    return first(data);
  }

  public async deleteSpaceMeeting(id: string) {
    if (!id || id === "") throw new Error("Meeting id is required");
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { error } = await this._client.from("space_meeting").delete().eq("id", id);

    if (error) throw new Error("Error deleting space meeting");

    return true;
  }

  public async deleteSpaceMeetingByEventId(eventId: string) {
    if (!eventId || eventId === "") throw new Error("Meeting id is required");
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    /** locate the meeting based on the event id */
    const meeting = await this.getSpaceMeetingByEventId(eventId);

    /** if we don't find the meeting the just return without doing anything */
    if (!meeting) return true;

    /** attempt to delete the event that was located in our database */
    const success = await this.deleteSpaceMeeting(meeting.id);

    /** return the result from the delete function */
    return success;
  }

  public async getSpaceMembers(spaceId: string): Promise<MemberAuthorization[]> {
    if (!spaceId || spaceId === "") throw new Error("Space id is required");
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { data, error } = await this._client
      .from("member_authorization")
      .select("*, user_profile: member_id(*)")
      .eq("authorized_entity_type", "space")
      .eq("authorized_entity_id", spaceId)
      .eq("is_base_access_level", true)
      .returns<MemberAuthorization[]>();

    if (error) throw new Error("Error getting space members");

    return data;
  }

  public async getTodaysMeetings() {
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const now = DateTime.local();

    const { data, error } = await this._client
      .from("space_meeting")
      .select("*, space: space_id(name)")
      .gte("start_date", now.toJSDate().toISOString())
      .lte("end_date", now.set({ hour: 23, minute: 59, second: 59 }).toJSDate().toISOString())
      .order("start_date", { ascending: false });

    if (error) throw new Error("Error getting todays meetings");

    return data;
  }

  public async getAccessLevelPermissions(): Promise<AccessLevelPermission[]> {
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { data, error } = await this._client.from("access_level_permission").select("*");

    if (error) throw new Error(error.message);

    this._accessLevelPermissions = data as AccessLevelPermission[];

    return data as AccessLevelPermission[];
  }

  public async hasPermission(
    entityType: string,
    entityId: string,
    permissionCode: string
  ): Promise<boolean> {
    // get the current user id from our store
    const userId = useAddInStore.getState().user?.id;

    if (!userId || userId === "") throw new Error("User id is required");
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { data } = await this._client
      .from("member_authorization")
      .select("*")
      .eq("member_id", userId)
      .eq("authorized_entity_type", entityType)
      .eq("authorized_entity_id", entityId)
      .eq("is_base_access_level", true)
      .throwOnError();

    /** grab the permission */
    const accessLevelPermissions = !this._accessLevelPermissions
      ? await this.getAccessLevelPermissions()
      : this._accessLevelPermissions;

    /** check if the user has the permission */
    return data?.some((ma: MemberAuthorization) => {
      const permission = accessLevelPermissions.find(
        (permission) =>
          permission.access_level === ma.access_level &&
          permission.entity_type === ma.authorized_entity_type &&
          permission.permission_code.toLowerCase() === permissionCode.toLowerCase()
      );
      return permission !== undefined;
    });
  }

  public async hasPermissionInAnyOrganization(permissionCode: string): Promise<boolean> {
    // get the current user id from our store
    const userId = useAddInStore.getState()?.user?.id;

    if (!userId || userId === "") throw new Error("User id is required");
    if (!this._client) await this.setSession();
    if (!this._client) throw new Error("Client is not initialized");

    const { data } = await this._client
      .from("member_authorization")
      .select("*")
      .eq("member_id", userId)
      .eq("authorized_entity_type", "organization")
      .eq("is_base_access_level", true)
      .throwOnError();

    /** grab the permission */
    const accessLevelPermissions = !this._accessLevelPermissions
      ? await this.getAccessLevelPermissions()
      : this._accessLevelPermissions;

    /** check if the user has the permission */
    return data?.some((ma: MemberAuthorization) => {
      const permission = accessLevelPermissions.find(
        (permission) =>
          permission.access_level === ma.access_level &&
          permission.entity_type === ma.authorized_entity_type &&
          permission.permission_code.toLowerCase() === permissionCode.toLowerCase()
      );
      return permission !== undefined;
    });
  }

  public connectToOnboardingBroadcast(callback: (payload: OnboardingBroadcastMessage) => void) {
    try {
      // get the current user id from our store
      const userId = useAddInStore.getState()?.user?.id;

      if (!userId || userId === "") throw new Error("User id is required");
      if (this._onboardingBroadcastListener) this._onboardingBroadcastListener?.unsubscribe();

      const channelName = `cadchat_onboarding:user_profile:${userId}`;
      const event = `cadchat_onboarding:${userId}`;

      if (!this._client) {
        this.setSession().then(() => {
          this._onboardingBroadcastListener = this._client
            .channel(channelName)
            .on("broadcast", { event }, (data: BroadcastPayload) => {
              callback(data.payload as OnboardingBroadcastMessage);
            });

          this._onboardingBroadcastListener?.subscribe(() => {
            logger.debug("Subscribed to onboarding broadcast...");
          });
        });
      } else {
        this._onboardingBroadcastListener = this._client
          .channel(channelName)
          .on("broadcast", { event }, (data: BroadcastPayload) => {
            callback(data.payload as OnboardingBroadcastMessage);
          });

        this._onboardingBroadcastListener?.subscribe(() => {
          logger.debug("Subscribed to onboarding broadcast...");
        });
      }
    } catch (error) {
      logger.error("Error subscribing to onboarding broadcast", error);
    }
  }

  public disconnectFromOnboardingBroadcast() {
    if (this._onboardingBroadcastListener) {
      this._onboardingBroadcastListener.unsubscribe();
      this._onboardingBroadcastListener = undefined;
      logger.debug("Unsubscribed from onboarding broadcast");
    }
  }

  private isEmail(str: string): boolean {
    const EMAIL_REGEX = /\S+@\S+\.\S+/;
    return EMAIL_REGEX.test(str);
  }

  /**
   * Keeps the Supabase SDK-generated Storage route and query parameters, but
   * normalizes the origin to the configured Supabase URL. This matters locally
   * because the app is configured against `127.0.0.1`, while SDK-generated URLs
   * can otherwise use `localhost`.
   *
   * @param {string} publicUrl The public URL generated by Supabase JS.
   * @returns {string} Returns the public URL with the configured Supabase origin.
   */
  private normalizeSupabaseStoragePublicUrl(publicUrl: string) {
    try {
      const configuredBaseUrl = envUtils.SUPABASE_BASE_URL;
      if (!configuredBaseUrl) return publicUrl;

      const configuredUrl = new URL(configuredBaseUrl);
      const url = new URL(publicUrl);
      if (!url.pathname.startsWith("/storage/v1/")) return publicUrl;

      return `${configuredUrl.origin}${url.pathname}${url.search}${url.hash}`;
    } catch {
      return publicUrl;
    }
  }
}

export const supabaseApi = new SupabaseApi();
